import { v4 as uuidv4 } from 'uuid';
import { ONE_CLICK_MV_V2_MAX_IMAGE_COUNT, TTS_EMOTIONS, toolTaskSupportsTextPrompt } from '@/app/constants';
import type {
  FormAudioPrompt,
  FormImagePrompt,
  ImageRange,
  OneClickMvSubtitleLanguage,
  TaskInputPrompts,
  TaskParams,
  ToolFormParams,
} from '@/app/types';
import { estimateLipSyncTextDurationSeconds } from '../estimate-lip-sync-text-duration';
import { hasUploadedImagePrompt } from '../temp-image-uri';
import { isHevcSupported } from './device-utils';
import { offlineAvatarHasBuiltInAudio } from './offline-avatar-audio';

const OFFLINE_AVATAR_MIN_DURATION = 5;

const buildMaterialTaskInputPromptFromFormImage = (
  ref: FormImagePrompt,
  fallbackName: string,
): TaskInputPrompts | null => {
  if (!ref.material?.id) {
    return null;
  }

  const content = ref.prompt?.selectedImage || ref.prompt?.sourceImage || '';

  return {
    type: 'material',
    content,
    name: ref.material.name || fallbackName,
    material: {
      id: ref.material.id,
      version: ref.material.version,
      share_id: ref.material.share_id,
    },
  };
};

type TTSPrompt = {
  type: 'text';
  content: string;
  audio?: {
    emotion?: string;
    pitch: number;
  };
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const TTS_EMOTION_PROMPT_REGEX = new RegExp(
  `\\{(${TTS_EMOTIONS.map(({ id }) => escapeRegExp(id)).join('|')})\\}([\\s\\S]*?)\\{/\\1\\}`,
  'g',
);

const pushTTSPrompt = (prompts: TTSPrompt[], prompt: TTSPrompt) => {
  if (!prompt.content) return;
  prompts.push(prompt);
};

type OneClickMvApiMode = 'unspecified' | 'sing' | 'sing_perform' | 'perform' | 'dance';
type OneClickMvApiSettings = {
  mode?: OneClickMvApiMode;
  subtitle_enable?: boolean;
  subtitle_color?: string;
  subtitle_language?: OneClickMvSubtitleLanguage;
};

const ONE_CLICK_MV_MODE_MAP: Record<Exclude<ToolFormParams['oneClickMvScene'], undefined>, OneClickMvApiMode> = {
  sing: 'sing',
  sing_act: 'sing_perform',
  dance: 'dance',
  performance: 'perform',
};

type OneClickMvRolePrompt = TaskInputPrompts & {
  lip_sync: {
    enable: boolean;
    index: number;
  };
};

type UsableImagePrompt = FormImagePrompt & {
  status: 'done';
  prompt: NonNullable<FormImagePrompt['prompt']>;
};

const hasUsableImagePrompt = (ref: FormImagePrompt): ref is UsableImagePrompt => {
  return ref.status === 'done' && hasUploadedImagePrompt(ref.prompt);
};

type UsableAudioPrompt = ToolFormParams['audioPrompts'][number] & {
  status: 'done';
  prompt: NonNullable<ToolFormParams['audioPrompts'][number]['prompt']>;
};

const hasUsableAudioPrompt = (ref: ToolFormParams['audioPrompts'][number]): ref is UsableAudioPrompt => {
  return ref.status === 'done' && !!ref.prompt?.src;
};

const buildOfflineAvatarAssetPrompt = (avatarId: string): TaskInputPrompts => ({
  type: 'offline_avatar',
  content: '',
  reference: {
    offline_avatar_id: avatarId,
  },
});

const getOfflineAvatarIdFromPrompt = (prompt: TaskInputPrompts): string => {
  if (prompt.type !== 'offline_avatar') return '';

  return prompt.reference?.offline_avatar_id?.trim() || '';
};

const buildOfflineAvatarTimelinePrompt = (
  actions: ToolFormParams['offlineAvatarTimeline'] | undefined,
  duration?: number,
): TaskInputPrompts | null => {
  const maxDuration =
    typeof duration === 'number' && Number.isFinite(duration) ? Math.max(0, Math.round(duration)) : undefined;
  const normalizedActions = (actions || [])
    .map((action) => ({
      text: action.text.trim(),
      start: Math.min(maxDuration ?? Number.POSITIVE_INFINITY, Math.max(0, Math.round(action.start))),
      end: Math.min(maxDuration ?? Number.POSITIVE_INFINITY, Math.max(0, Math.round(action.end))),
    }))
    .filter((action) => action.text && action.end > action.start);

  if (normalizedActions.length === 0) {
    return null;
  }

  return {
    type: 'timeline',
    content: '',
    offline_avatar_timeline: {
      actions: normalizedActions,
    },
  };
};

const getOfflineAvatarSubmitSettings = (settings: ToolFormParams['settings']): ToolFormParams['settings'] => {
  const { aspect_ratio: _aspectRatio, ...submitSettings } = settings;
  return submitSettings;
};

const OFFLINE_AVATAR_RECREATE_ASPECT_RATIOS = [
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
] as const;

const getTaskPromptSelectedImageRange = (prompt: TaskInputPrompts): ImageRange | undefined => {
  if (!prompt.selected_region) return undefined;

  return [
    prompt.selected_region.top_left.x,
    prompt.selected_region.top_left.y,
    prompt.selected_region.bottom_right.x,
    prompt.selected_region.bottom_right.y,
  ];
};

const getOfflineAvatarAspectRatioFromRange = (range?: ImageRange): string | undefined => {
  if (!range) return undefined;

  const width = range[2] - range[0];
  const height = range[3] - range[1];

  if (width <= 0 || height <= 0) return undefined;

  const ratio = width / height;
  return OFFLINE_AVATAR_RECREATE_ASPECT_RATIOS.reduce((closest, option) =>
    Math.abs(option.value - ratio) < Math.abs(closest.value - ratio) ? option : closest,
  ).label;
};

const createOfflineAvatarImagePromptFromTaskPrompt = (prompt: TaskInputPrompts): FormImagePrompt | null => {
  if (prompt.type !== 'image' || !prompt.content) return null;

  const sourceImage = prompt.src_imgs?.[0] || prompt.src_img || prompt.content;
  const referenceCreationId = prompt.reference?.creation_id;

  return {
    id: referenceCreationId || prompt.content,
    status: 'done',
    prompt: {
      sourceImage,
      sourceImagePreview: sourceImage,
      selectedImage: prompt.content,
      selectedImagePreview: prompt.content,
      selectedImageRange: getTaskPromptSelectedImageRange(prompt),
    },
    reference: referenceCreationId
      ? {
          creation_id: referenceCreationId,
        }
      : undefined,
  };
};

const createOfflineAvatarAudioPromptFromTaskPrompt = (
  prompt: TaskInputPrompts,
  settings?: TaskParams['settings'],
): FormAudioPrompt | null => {
  if (prompt.type !== 'audio' || !prompt.content) return null;

  return {
    id: prompt.content,
    status: 'done',
    prompt: {
      src: prompt.content,
      preview: prompt.content,
      name: prompt.name,
      duration: typeof settings?.duration === 'number' ? settings.duration : undefined,
    },
  };
};

const createOfflineAvatarAssetFromTask = (input: TaskParams['input']): ToolFormParams['offlineAvatar'] | undefined => {
  const avatarId = input.prompts?.map((prompt) => getOfflineAvatarIdFromPrompt(prompt)).find((id) => id);
  if (!avatarId) return undefined;

  return {
    id: avatarId,
    name: '',
    live_image: '',
    avatar_type: 'user',
  };
};

const isFormImagePrompt = (prompt: FormImagePrompt | null): prompt is FormImagePrompt => prompt !== null;

const isFormAudioPrompt = (prompt: FormAudioPrompt | null): prompt is FormAudioPrompt => prompt !== null;

const createOfflineAvatarTimelineActions = (
  input: TaskParams['input'],
): NonNullable<ToolFormParams['offlineAvatarTimeline']> => {
  return (
    input.prompts
      ?.find((prompt) => prompt.type === 'timeline')
      ?.offline_avatar_timeline?.actions?.map((action) => ({
        id: uuidv4(),
        text: action.text?.trim() || '',
        start: action.start ?? 0,
        end: action.end ?? 0,
      }))
      .filter((action) => action.text && action.end > action.start) || []
  );
};

const getOfflineAvatarRecreateSettings = ({
  settings,
  imagePrompts,
}: {
  settings?: TaskParams['settings'];
  imagePrompts: FormImagePrompt[];
}): ToolFormParams['settings'] => {
  const firstImageRange = imagePrompts[0]?.prompt?.selectedImageRange;
  const aspectRatio = settings?.aspect_ratio || getOfflineAvatarAspectRatioFromRange(firstImageRange) || '16:9';

  return {
    ...settings,
    resolution: settings?.resolution || '720p',
    aspect_ratio: aspectRatio,
  } as ToolFormParams['settings'];
};

const convertTextPromptToTTSPrompts = ({ textPrompt, pitch }: { textPrompt?: string; pitch: number }): TTSPrompt[] => {
  const rawText = textPrompt || '';
  if (!rawText) return [];

  const prompts: TTSPrompt[] = [];
  let lastIndex = 0;

  for (const match of rawText.matchAll(TTS_EMOTION_PROMPT_REGEX)) {
    const [matchedText, emotion, content] = match;
    const matchIndex = match.index ?? 0;

    pushTTSPrompt(prompts, {
      type: 'text',
      content: rawText.slice(lastIndex, matchIndex),
      audio: {
        pitch,
      },
    });

    pushTTSPrompt(prompts, {
      type: 'text',
      content,
      audio: {
        emotion,
        pitch,
      },
    });

    lastIndex = matchIndex + matchedText.length;
  }

  pushTTSPrompt(prompts, {
    type: 'text',
    content: rawText.slice(lastIndex),
    audio: {
      pitch,
    },
  });

  return prompts;
};

export const convertTTSPromptsToTextPrompt = (prompts?: TaskInputPrompts[]): string => {
  return (prompts || [])
    .filter((prompt): prompt is TTSPrompt => prompt.type === 'text')
    .map((prompt) => {
      const emotion = prompt.audio?.emotion;
      if (!emotion) {
        return prompt.content;
      }

      return `{${emotion}}${prompt.content}{/${emotion}}`;
    })
    .join('');
};

export const buildTTSRecreatePatch = ({
  input,
  settings,
}: {
  input: TaskParams['input'];
  settings?: TaskParams['settings'];
}): Pick<ToolFormParams, 'settings' | 'textPrompt'> => ({
  settings: {
    ...settings,
    pitch: input.prompts?.find((prompt) => prompt.type === 'text')?.audio?.pitch || 0,
  } as ToolFormParams['settings'],
  textPrompt: convertTTSPromptsToTextPrompt(input.prompts),
});

export const buildOfflineAvatarRecreatePatch = ({
  input,
  settings,
}: {
  input: TaskParams['input'];
  settings?: TaskParams['settings'];
}): Pick<
  ToolFormParams,
  | 'imagePrompts'
  | 'audioPrompts'
  | 'offlineAvatarTimeline'
  | 'settings'
  | 'offlineAvatar'
  | 'textPrompt'
  | 'offlineAvatarInputMode'
> => {
  const imagePrompts =
    input.prompts?.map((prompt) => createOfflineAvatarImagePromptFromTaskPrompt(prompt)).filter(isFormImagePrompt) ||
    [];
  const audioPrompts =
    input.prompts
      ?.map((prompt) => createOfflineAvatarAudioPromptFromTaskPrompt(prompt, settings))
      .filter(isFormAudioPrompt) || [];
  const textPrompt = input.prompts?.find((prompt) => prompt.type === 'text')?.content || '';

  return {
    imagePrompts,
    audioPrompts,
    textPrompt,
    offlineAvatarInputMode: textPrompt ? 'text' : 'audio',
    offlineAvatarTimeline: createOfflineAvatarTimelineActions(input),
    offlineAvatar: createOfflineAvatarAssetFromTask(input),
    settings: getOfflineAvatarRecreateSettings({ settings, imagePrompts }),
  };
};

export const convertToolTask = ({
  taskType,
  formParams,
}: {
  taskType: TaskParams['type'];
  formParams: ToolFormParams;
}): TaskParams => {
  // Handle text2tts separately
  if (taskType === 'tts') {
    const ttsSettings = formParams.settings as ToolFormParams['settings'] & { pitch?: number };
    const prompts = convertTextPromptToTTSPrompts({
      textPrompt: formParams.textPrompt,
      pitch: typeof ttsSettings.pitch === 'number' ? ttsSettings.pitch : 0,
    });
    const plainText = prompts.map((prompt) => prompt.content).join('');

    return {
      input: {
        prompts,
        enhance: true,
      },
      settings: {
        ...formParams.settings,
      },
      type: taskType,
      text: plainText,
      ...(formParams.extraParams ? { extra_params: formParams.extraParams } : {}),
    };
  }

  if (taskType === 'offline_avatar') {
    const inputMode = formParams.offlineAvatarInputMode ?? 'audio';
    const textPrompt = formParams.textPrompt?.trim() || '';
    const audioDuration = formParams.audioPrompts.find(
      (ref) => ref.status === 'done' && typeof ref.prompt?.duration === 'number',
    )?.prompt?.duration;
    const prompts: TaskInputPrompts[] = [];
    const avatarId = formParams.offlineAvatar?.id?.trim();
    const avatarHasBuiltInAudio = offlineAvatarHasBuiltInAudio(formParams.offlineAvatar);
    const {
      voice_id: selectedVoiceId,
      speed: _speed,
      volume: _volume,
      ...baseSubmitSettings
    } = getOfflineAvatarSubmitSettings(formParams.settings);
    const sampleCount = Number(baseSubmitSettings.sample_count) || 1;
    const duration =
      inputMode === 'text'
        ? estimateLipSyncTextDurationSeconds(textPrompt)
        : typeof audioDuration === 'number'
          ? Math.max(OFFLINE_AVATAR_MIN_DURATION, Math.round(audioDuration))
          : undefined;
    const timelinePrompt = buildOfflineAvatarTimelinePrompt(formParams.offlineAvatarTimeline, duration);

    if (!avatarId) {
      formParams.imagePrompts.forEach((ref, idx) => {
        if (ref.status !== 'done' || !ref.prompt?.selectedImage || !ref.prompt.sourceImage) return;
        prompts.push({
          type: 'image',
          content: ref.prompt.selectedImage,
          src_imgs: [ref.prompt.sourceImage],
          name: `图片${idx + 1}`,
          reference: ref.reference,
        });
      });
    }

    if (inputMode === 'text') {
      if (textPrompt) {
        prompts.push({
          type: 'text',
          content: textPrompt,
        });
      }
    } else {
      formParams.audioPrompts.forEach((ref, idx) => {
        if (!hasUsableAudioPrompt(ref)) return;
        prompts.push({
          type: 'audio',
          content: ref.prompt.src,
          name: `音频${idx + 1}`,
        });
      });
    }

    if (avatarId) {
      prompts.push(buildOfflineAvatarAssetPrompt(avatarId));
    }

    if (timelinePrompt) {
      prompts.push(timelinePrompt);
    }

    const extraParams =
      inputMode === 'text' && avatarHasBuiltInAudio
        ? (() => {
            const { is_my_voice: _isMyVoice, ...rest } = formParams.extraParams || {};
            return Object.keys(rest).length ? rest : undefined;
          })()
        : inputMode === 'text'
          ? formParams.extraParams
          : undefined;

    return {
      input: {
        prompts,
        enhance: true,
      },
      settings: {
        ...baseSubmitSettings,
        sample_count: sampleCount,
        ...(inputMode === 'text' && !avatarHasBuiltInAudio && selectedVoiceId ? { voice_id: selectedVoiceId } : {}),
        ...(duration !== undefined ? { duration } : {}),
        codec: isHevcSupported() ? 'h265' : 'h264',
      },
      type: taskType,
      ...(extraParams ? { extra_params: extraParams } : {}),
    };
  }

  const isOneClickMvV2 = taskType === 'music_video' && formParams.oneClickMvVersion === 'v2';
  const isOneClickMvLipSyncScene =
    isOneClickMvV2 && (formParams.oneClickMvScene === 'sing' || formParams.oneClickMvScene === 'sing_act');
  const existingMusicVideoSettings = (
    formParams.settings as TaskParams['settings'] & {
      music_video?: OneClickMvApiSettings;
    }
  ).music_video;
  const musicVideoSettings =
    taskType === 'music_video'
      ? isOneClickMvV2
        ? {
            ...existingMusicVideoSettings,
            mode: ONE_CLICK_MV_MODE_MAP[formParams.oneClickMvScene || 'sing'],
            subtitle_enable: formParams.oneClickMvSubtitleEnabled ?? false,
            ...(formParams.oneClickMvSubtitleEnabled ? { subtitle_color: '#FFFFFF' } : {}),
            ...(formParams.oneClickMvSubtitleEnabled && formParams.oneClickMvSubtitleLanguage
              ? { subtitle_language: formParams.oneClickMvSubtitleLanguage }
              : {}),
          }
        : {
            ...existingMusicVideoSettings,
            mode: 'unspecified' as const,
          }
      : undefined;
  const uploadedAudioDuration = formParams.audioPrompts.find(hasUsableAudioPrompt)?.prompt.duration;
  const settings = isOneClickMvV2
    ? ({
        ...formParams.settings,
        music_video: musicVideoSettings,
        ...(typeof uploadedAudioDuration === 'number' ? { duration: Math.round(uploadedAudioDuration) } : {}),
      } as TaskParams['settings'] & {
        music_video: {
          mode: OneClickMvApiMode;
        };
      })
    : taskType === 'music_video' && typeof uploadedAudioDuration === 'number'
      ? {
          ...formParams.settings,
          music_video: musicVideoSettings,
          duration: Math.round(uploadedAudioDuration),
        }
      : formParams.settings;
  const {
    imagePrompts,
    videoPrompts,
    audioPrompts,
    textPrompt,
    remixStyle,
    subtitlePrompts,
    referenceImagePrompts,
    extraParams,
  } = formParams;
  const prompts: TaskInputPrompts[] = [];

  if (toolTaskSupportsTextPrompt(taskType) && textPrompt) {
    prompts.push({ type: 'text', content: textPrompt });
  }

  if (taskType === 'style_transfer' && remixStyle) {
    prompts.push({
      type: 'remix_style',
      content: remixStyle.name || remixStyle.id,
      remix_style: {
        id: remixStyle.id,
        name: remixStyle.name || '',
        url: remixStyle.url || '',
      },
    });
  }

  videoPrompts
    .filter((p) => p.id !== formParams.creationId)
    .forEach((ref, idx) => {
      if (ref.status !== 'done' || !ref.prompt) return;
      prompts.push({
        type: 'video',
        content: ref.prompt.video,
        name: `视频${idx + 1}`,
        cover_uri: ref.prompt.coverImage,
        reference: ref.reference,
      });
    });

  if (isOneClickMvLipSyncScene) {
    referenceImagePrompts?.filter(hasUsableImagePrompt).forEach((ref, idx) => {
      prompts.push({
        type: 'image',
        content: ref.prompt.selectedImage,
        src_imgs: [ref.prompt.sourceImage],
        name: `对口型角色${idx + 1}`,
        lip_sync: {
          enable: true,
          index: idx,
        },
      } as OneClickMvRolePrompt);
    });
  }

  imagePrompts
    .filter((p) => p.id !== formParams.creationId)
    .slice(0, isOneClickMvV2 ? ONE_CLICK_MV_V2_MAX_IMAGE_COUNT : undefined)
    .forEach((ref, idx) => {
      if (isOneClickMvV2 && !hasUsableImagePrompt(ref)) return;
      if (ref.status !== 'done' || !ref.prompt) return;

      const materialPrompt = buildMaterialTaskInputPromptFromFormImage(ref, `图片${idx + 1}`);
      if (materialPrompt) {
        prompts.push(materialPrompt);
        return;
      }
      if (!hasUploadedImagePrompt(ref.prompt)) return;

      const prompt: TaskInputPrompts = {
        type: 'image',
        content: ref.prompt.selectedImage,
        src_imgs: [ref.prompt.sourceImage],
        name: `图片${idx + 1}`,
        reference: ref.reference,
      };

      if (ref.prompt.selectedImageRange) {
        prompt.selected_region = {
          top_left: { x: ref.prompt.selectedImageRange[0], y: ref.prompt.selectedImageRange[1] },
          bottom_right: { x: ref.prompt.selectedImageRange[2], y: ref.prompt.selectedImageRange[3] },
        };
      }
      prompts.push(prompt);
    });

  audioPrompts.forEach((ref, idx) => {
    if (!hasUsableAudioPrompt(ref)) return;
    prompts.push({
      type: 'audio',
      content: ref.prompt.src,
      name: `音频${idx + 1}`,
    });
  });

  if (!isOneClickMvV2) {
    subtitlePrompts?.forEach((ref) => {
      if (ref.status !== 'done' || !ref.prompt?.src) return;
      prompts.push({
        type: 'subtitle',
        content: ref.prompt.src,
      });
    });
  }

  return {
    input: {
      creation_id: !!formParams.creationId && formParams.creationId !== '0' ? formParams.creationId : undefined,
      prompts,
      enhance: true,
      ...(isOneClickMvLipSyncScene ? { lip_sync: true } : {}),
    },
    settings: {
      ...settings,
      ...(taskType && taskType !== 'upscale_image' ? { codec: isHevcSupported() ? 'h265' : 'h264' } : {}),
    },
    type: taskType,
    ...(extraParams && taskType !== 'music_video'
      ? {
          extra_params: extraParams,
        }
      : {}),
  };
};
